import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  RotateCw, 
  Plus, 
  Edit, 
  Trash2, 
  Play,
  Pause,
  AlertCircle,
  GripVertical,
  X,
  Eye,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { parseLocalDate } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RotationManagement = ({ isManagerOrAdmin, currentUser }) => {
  const [rotations, setRotations] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedRotation, setSelectedRotation] = useState(null);
  const [editingRotation, setEditingRotation] = useState(null);
  
  const [rotationForm, setRotationForm] = useState({
    name: '',
    rotation_shift_template_id: '',
    day_template_overrides: {},  // {day_num_str: template_id} for per-day templates
    employee_shift_assignments: [],
    rotation_length_weeks: 1,
    days_of_week: [0, 1, 2, 3, 4],
    start_date: new Date().toISOString().split('T')[0],
    week_pattern: null,
    rotating_employee_only: false
  });
  
  const [useAdvancedPattern, setUseAdvancedPattern] = useState(false);
  const [useDayTemplates, setUseDayTemplates] = useState(false);
  const [patternLength, setPatternLength] = useState(10);
  
  const [applyForm, setApplyForm] = useState({
    rotation_id: '',
    weeks_to_generate: 12,
    duration_mode: 'weeks',
    end_date: ''
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState({});
  
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchRotations();
    fetchShiftTemplates();
    fetchUsers();
  }, []);

  const fetchRotations = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/rotations`, { headers });
      setRotations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch rotations:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied - Manager role required');
      }
    }
  };

  const fetchShiftTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/shift-templates`, { headers });
      setShiftTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch shift templates:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      // Filter out specific test accounts only
      const testAccounts = ['testadmin@workmanager.com', 'testuser@company.com'];
      const activeUsers = (response.data || []).filter(u => !testAccounts.includes(u.email?.toLowerCase()));
      setUsers(activeUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateRotation = async () => {
    try {
      if (!rotationForm.name || !rotationForm.rotation_shift_template_id || rotationForm.employee_shift_assignments.length === 0) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      // Validate all employees have default templates assigned
      const missingTemplates = rotationForm.employee_shift_assignments.filter(a => !a.default_shift_template_id);
      if (missingTemplates.length > 0) {
        toast.error('Please assign default shift templates to all employees');
        return;
      }
      
      if (rotationForm.days_of_week.length === 0) {
        toast.error('Please select at least one day of the week');
        return;
      }
      
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingRotation) {
        // Update existing rotation
        console.log('[Rotation Update] Sending:', { rotating_employee_only: rotationForm.rotating_employee_only });
        await axios.put(`${API}/rotations/${editingRotation.id}`, rotationForm, { headers });
        toast.success('Rotation updated successfully');
      } else {
        // Create new rotation
        await axios.post(`${API}/rotations`, rotationForm, { headers });
        toast.success('Rotation created successfully');
      }
      
      setShowCreateModal(false);
      setEditingRotation(null);
      resetForm();
      fetchRotations();
    } catch (error) {
      console.error('Failed to save rotation:', error);
      toast.error(error.response?.data?.detail || 'Failed to save rotation');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRotation = (rotation) => {
    const hasPattern = rotation.week_pattern && rotation.week_pattern.length > 0;
    const hasDayOverrides = rotation.day_template_overrides && Object.keys(rotation.day_template_overrides).length > 0;
    setRotationForm({
      name: rotation.name,
      rotation_shift_template_id: rotation.rotation_shift_template_id,
      day_template_overrides: rotation.day_template_overrides || {},
      employee_shift_assignments: rotation.employee_shift_assignments || [],
      rotation_length_weeks: rotation.rotation_length_weeks,
      days_of_week: rotation.days_of_week || [],
      start_date: rotation.start_date,
      week_pattern: rotation.week_pattern || null,
      rotating_employee_only: rotation.rotating_employee_only || false
    });
    setUseAdvancedPattern(hasPattern);
    setUseDayTemplates(hasDayOverrides);
    if (hasPattern) {
      setPatternLength(rotation.week_pattern.length);
    }
    setEditingRotation(rotation);
    setShowCreateModal(true);
  };

  const handleApplyRotation = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let weeksToGenerate = applyForm.weeks_to_generate;
      if (applyForm.duration_mode === 'endDate' && applyForm.end_date) {
        const startDate = parseLocalDate(selectedRotation.start_date);
        const endDate = parseLocalDate(applyForm.end_date);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        weeksToGenerate = Math.ceil(diffDays / 7);
      }
      
      const response = await axios.post(
        `${API}/rotations/${applyForm.rotation_id}/apply`,
        { rotation_id: applyForm.rotation_id, weeks_to_generate: weeksToGenerate },
        { headers }
      );
      
      const result = response.data;
      
      // Log detailed info for debugging
      console.log('[Rotation Apply] Full result:', result);
      if (result.rotation_config) {
        console.log('[Rotation Apply] Stored rotation days_of_week:', result.rotation_config.days_of_week, result.rotation_config.day_names);
      }
      if (result.day_summary) {
        console.log('[Rotation Apply] Day summary:', result.day_summary);
      }
      
      const dayCount = Object.keys(result.day_summary || {}).length;
      const config = result.rotation_config || {};
      const storedDays = config.day_names || [];
      const hasWeekends = config.days_of_week?.includes(5) && config.days_of_week?.includes(6);
      
      if (result.conflicts && result.conflicts.length > 0) {
        toast.warning(
          `${result.shifts_created} shifts created over ${dayCount} days (${result.conflicts.length} conflicts). Days configured: ${storedDays.join(', ')}`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `${result.shifts_created} shifts created over ${dayCount} days. Days: ${storedDays.join(', ')}`,
          { duration: 5000 }
        );
      }
      
      // Warn if weekends not configured
      if (!hasWeekends && config.days_of_week?.length > 0) {
        toast.info('Note: This rotation does not include weekends (Sat/Sun). Edit the rotation to add them if needed.', { duration: 6000 });
      }
      
      setShowApplyModal(false);
      setPreview(null);
      setPreviewExpanded({});
      setApplyForm({ rotation_id: '', weeks_to_generate: 12, duration_mode: 'weeks', end_date: '' });
    } catch (error) {
      console.error('Failed to apply rotation:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply rotation');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewRotation = async () => {
    try {
      setPreviewLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let weeksToGenerate = applyForm.weeks_to_generate;
      if (applyForm.duration_mode === 'endDate' && applyForm.end_date) {
        const startDate = parseLocalDate(selectedRotation.start_date);
        const endDate = parseLocalDate(applyForm.end_date);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        weeksToGenerate = Math.ceil(diffDays / 7);
      }
      
      const response = await axios.post(
        `${API}/rotations/${applyForm.rotation_id}/preview`,
        { rotation_id: applyForm.rotation_id, weeks_to_generate: Math.min(weeksToGenerate, 8) },
        { headers }
      );
      setPreview(response.data);
      // Auto-expand first 4 weeks
      const exp = {};
      response.data.weeks.slice(0, 4).forEach((_, i) => { exp[i] = true; });
      setPreviewExpanded(exp);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteRotation = async (rotationId) => {
    if (!window.confirm('Are you sure you want to delete this rotation? This will not delete existing scheduled shifts.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/rotations/${rotationId}`, { headers });
      toast.success('Rotation deleted successfully');
      fetchRotations();
    } catch (error) {
      console.error('Failed to delete rotation:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete rotation');
    }
  };

  const handleToggleActive = async (rotation) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/rotations/${rotation.id}`,
        { is_active: !rotation.is_active },
        { headers }
      );
      toast.success(`Rotation ${rotation.is_active ? 'paused' : 'activated'}`);
      fetchRotations();
    } catch (error) {
      console.error('Failed to toggle rotation:', error);
      toast.error('Failed to update rotation');
    }
  };

  const resetForm = () => {
    setRotationForm({
      name: '',
      rotation_shift_template_id: '',
      day_template_overrides: {},
      employee_shift_assignments: [],
      rotation_length_weeks: 1,
      days_of_week: [0, 1, 2, 3, 4],
      start_date: new Date().toISOString().split('T')[0],
      week_pattern: null
    });
    setUseAdvancedPattern(false);
    setUseDayTemplates(false);
    setPatternLength(10);
  };

  const addEmployee = (userId) => {
    const alreadyAdded = rotationForm.employee_shift_assignments.find(a => a.employee_id === userId);
    if (!alreadyAdded) {
      setRotationForm({
        ...rotationForm,
        employee_shift_assignments: [
          ...rotationForm.employee_shift_assignments,
          { employee_id: userId, default_shift_template_id: '' }
        ]
      });
    }
  };

  const removeEmployee = (userId) => {
    setRotationForm({
      ...rotationForm,
      employee_shift_assignments: rotationForm.employee_shift_assignments.filter(a => a.employee_id !== userId)
    });
  };
  
  const updateEmployeeTemplate = (userId, templateId) => {
    setRotationForm({
      ...rotationForm,
      employee_shift_assignments: rotationForm.employee_shift_assignments.map(a =>
        a.employee_id === userId ? { ...a, default_shift_template_id: templateId } : a
      )
    });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newAssignments = [...rotationForm.employee_shift_assignments];
    const draggedItem = newAssignments[draggedIndex];
    newAssignments.splice(draggedIndex, 1);
    newAssignments.splice(index, 0, draggedItem);
    
    setRotationForm({ ...rotationForm, employee_shift_assignments: newAssignments });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const toggleDayOfWeek = (day) => {
    const days = rotationForm.days_of_week;
    if (days.includes(day)) {
      setRotationForm({
        ...rotationForm,
        days_of_week: days.filter(d => d !== day)
      });
    } else {
      setRotationForm({
        ...rotationForm,
        days_of_week: [...days, day].sort()
      });
    }
  };

  // Advanced Pattern Management
  const initializePattern = () => {
    if (rotationForm.employee_shift_assignments.length === 0) {
      toast.error('Please add employees to the rotation first');
      return;
    }
    // Initialize pattern with first employee for all weeks
    const firstEmployeeId = rotationForm.employee_shift_assignments[0].employee_id;
    const pattern = Array(patternLength).fill(firstEmployeeId);
    setRotationForm({
      ...rotationForm,
      week_pattern: pattern
    });
    setUseAdvancedPattern(true);
  };

  const updatePatternWeek = (weekIndex, employeeId) => {
    const newPattern = [...(rotationForm.week_pattern || [])];
    newPattern[weekIndex] = employeeId;
    setRotationForm({
      ...rotationForm,
      week_pattern: newPattern
    });
  };

  const changePatternLength = (newLength) => {
    const currentPattern = rotationForm.week_pattern || [];
    if (newLength > currentPattern.length) {
      // Extend pattern by repeating the existing pattern
      const extended = [...currentPattern];
      const firstEmployeeId = rotationForm.employee_shift_assignments[0]?.employee_id;
      while (extended.length < newLength) {
        const fillValue = currentPattern[extended.length % currentPattern.length] || firstEmployeeId;
        extended.push(fillValue);
      }
      setRotationForm({ ...rotationForm, week_pattern: extended });
    } else {
      // Truncate pattern
      setRotationForm({ ...rotationForm, week_pattern: currentPattern.slice(0, newLength) });
    }
    setPatternLength(newLength);
  };

  const disableAdvancedPattern = () => {
    setUseAdvancedPattern(false);
    setRotationForm({
      ...rotationForm,
      week_pattern: null
    });
  };


  const getDayName = (day) => {
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return names[day];
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.username || user?.email || 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shift Rotations</h2>
          <p className="text-gray-600 mt-1">
            Manage rotating shift schedules for your teams
          </p>
        </div>
        {isManagerOrAdmin && (
          <Button onClick={() => {
            setEditingRotation(null);
            resetForm();
            setShowCreateModal(true);
          }} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Rotation
          </Button>
        )}
      </div>

      {/* Rotations List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {rotations.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12">
              <div className="text-center">
                <RotateCw className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No rotations created yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  Create a rotation to automatically schedule shifts for your team
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          rotations.map((rotation) => {
            const rotationTemplate = shiftTemplates.find(t => t.id === rotation.rotation_shift_template_id);
            const employeeCount = rotation.employee_shift_assignments?.length || 0;
            
            return (
              <Card key={rotation.id} className="border border-gray-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center space-x-2">
                        <RotateCw className={`h-5 w-5 ${rotation.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                        <span>{rotation.name}</span>
                      </CardTitle>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600">
                          <strong>Rotation Shift:</strong> {rotationTemplate?.name || 'Unknown'} ({rotationTemplate?.start_time} - {rotationTemplate?.end_time})
                          <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                            rotationTemplate?.shift_type === 'on_call' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {rotationTemplate?.shift_type === 'on_call' ? 'On-Call' : 'Regular'}
                          </span>
                        </p>
                        {rotation.day_template_overrides && Object.keys(rotation.day_template_overrides).length > 0 && (
                          <div className="text-xs text-indigo-600 bg-indigo-50 p-2 rounded mt-1">
                            <strong>Per-Day Templates:</strong>{' '}
                            {Object.entries(rotation.day_template_overrides).sort(([a],[b]) => Number(a) - Number(b)).map(([dayNum, tmplId]) => {
                              const tmpl = shiftTemplates.find(t => t.id === tmplId);
                              return `${getDayName(Number(dayNum))}: ${tmpl?.name || 'Unknown'} (${tmpl?.start_time}-${tmpl?.end_time})`;
                            }).join(', ')}
                          </div>
                        )}
                        <p className="text-sm text-gray-600">
                          <strong>Employees:</strong> {employeeCount} (with individual default shifts)
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Rotation:</strong> {
                            rotation.week_pattern && rotation.week_pattern.length > 0 
                              ? `${rotation.week_pattern.length}-week custom pattern`
                              : `Every ${rotation.rotation_length_weeks} week(s)`
                          }
                        </p>
                        {rotation.week_pattern && rotation.week_pattern.length > 0 && (
                          <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mt-1">
                            <strong>Pattern Preview:</strong> {rotation.week_pattern.slice(0, 6).map((empId, idx) => {
                              const emp = users.find(u => u.id === empId);
                              return emp ? emp.username : 'Unknown';
                            }).join(' → ')}
                            {rotation.week_pattern.length > 6 && '...'}
                          </div>
                        )}
                        <p className="text-sm text-gray-600">
                          <strong>Days:</strong> {rotation.days_of_week?.map(d => getDayName(d)).join(', ')}
                        </p>
                        {rotation.rotating_employee_only && (
                          <p className="text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded mt-1 inline-block">
                            Rotating employee only
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant={rotation.is_active ? 'default' : 'secondary'}>
                      {rotation.is_active ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Default to pattern length if exists, otherwise 12 weeks
                        const defaultWeeks = rotation.week_pattern && rotation.week_pattern.length > 0 
                          ? rotation.week_pattern.length 
                          : 12;
                        setApplyForm({ rotation_id: rotation.id, weeks_to_generate: defaultWeeks, duration_mode: 'weeks', end_date: '' });
                        setSelectedRotation(rotation);
                        setShowApplyModal(true);
                      }}
                      disabled={!rotation.is_active}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Apply
                    </Button>
                    {isManagerOrAdmin && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditRotation(rotation)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleActive(rotation)}
                        >
                          {rotation.is_active ? (
                            <>
                              <Pause className="h-4 w-4 mr-1" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteRotation(rotation.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Rotation Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRotation ? 'Edit Rotation' : 'Create New Rotation'}</DialogTitle>
            <DialogDescription>
              {editingRotation ? 'Update the rotating shift schedule' : 'Set up a rotating shift schedule for your team'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="rotation-name">Rotation Name *</Label>
                <Input
                  id="rotation-name"
                  placeholder="e.g., Tier 2 Evening Coverage"
                  value={rotationForm.name}
                  onChange={(e) => setRotationForm({...rotationForm, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="rotation-shift-template">Rotation Shift Template * (the shift that rotates)</Label>
                  <Select
                    value={rotationForm.rotation_shift_template_id}
                    onValueChange={(value) => setRotationForm({...rotationForm, rotation_shift_template_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="e.g., 3pm-11pm afternoon shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {shiftTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name} ({template.start_time} - {template.end_time}) [{template.shift_type === 'on_call' ? 'On-Call' : template.shift_type === 'regular' ? 'Regular' : template.shift_type}]
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const selectedTemplate = shiftTemplates.find(t => t.id === rotationForm.rotation_shift_template_id);
                    if (selectedTemplate && selectedTemplate.shift_type !== 'on_call' && 
                        (selectedTemplate.name.toLowerCase().includes('on call') || selectedTemplate.name.toLowerCase().includes('on-call') || selectedTemplate.name.toLowerCase().includes('oncall'))) {
                      return (
                        <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                          <strong>Warning:</strong> "{selectedTemplate.name}" has shift type "<strong>{selectedTemplate.shift_type}</strong>" — shifts created from this template will appear as {selectedTemplate.shift_type} shifts, not on-call. 
                          Edit the template in Admin → Shift Templates to change its type to "on_call" if needed.
                        </div>
                      );
                    }
                    if (selectedTemplate) {
                      return <p className="text-xs text-gray-500 mt-1">Shift type: <strong>{selectedTemplate.shift_type === 'on_call' ? 'On-Call' : selectedTemplate.shift_type}</strong> — Employees work this shift during their rotation week</p>;
                    }
                    return <p className="text-xs text-gray-500 mt-1">Employees work this shift during their rotation week</p>;
                  })()}
                </div>
                
                <div>
                  <Label htmlFor="rotation-length">Rotation Length (weeks) *</Label>
                  <Input
                    id="rotation-length"
                    type="number"
                    min="1"
                    value={rotationForm.rotation_length_weeks}
                    onChange={(e) => setRotationForm({...rotationForm, rotation_length_weeks: parseInt(e.target.value) || 1})}
                    disabled={useAdvancedPattern}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {useAdvancedPattern ? 'Disabled when using advanced pattern' : 'How many weeks before rotating to next person'}
                  </p>
                </div>
              </div>
              
              {/* Rotating Employee Only toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div>
                  <Label htmlFor="rotating-employee-only" className="text-sm font-medium">Only create shifts for the rotating employee</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {rotationForm.rotating_employee_only 
                      ? 'Only the on-rotation employee gets shifts. Others are unaffected.' 
                      : 'All assigned employees get their default shifts each week. Turn ON for on-call rotations where other employees already have shifts scheduled separately.'}
                  </p>
                </div>
                <Switch
                  id="rotating-employee-only"
                  checked={rotationForm.rotating_employee_only}
                  onCheckedChange={(checked) => setRotationForm({...rotationForm, rotating_employee_only: checked})}
                  data-testid="rotating-employee-only-toggle"
                />
              </div>
              
              <div>
                <Label htmlFor="start-date">Start Date *</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={rotationForm.start_date}
                  onChange={(e) => setRotationForm({...rotationForm, start_date: e.target.value})}
                />
              </div>

              {/* Advanced Pattern Toggle */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-blue-900 font-semibold">Advanced Week-by-Week Pattern</Label>
                    <p className="text-xs text-blue-700 mt-1">
                      Create custom patterns like: Tech1, Tech2, Tech1, Tech3, Tech1, Tech4...
                    </p>
                  </div>
                  {!useAdvancedPattern ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={initializePattern}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Enable Pattern
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={disableAdvancedPattern}
                      className="bg-gray-500 text-white hover:bg-gray-600"
                    >
                      <X className="h-4 w-4 mr-1" /> Disable Pattern
                    </Button>
                  )}
                </div>

                {useAdvancedPattern && (
                  <div className="space-y-3 mt-3 pt-3 border-t border-blue-200">
                    <div className="flex items-center space-x-3">
                      <Label htmlFor="pattern-length" className="text-sm font-medium">Pattern Length:</Label>
                      <Input
                        id="pattern-length"
                        type="number"
                        min="1"
                        max="52"
                        value={patternLength}
                        onChange={(e) => changePatternLength(parseInt(e.target.value) || 1)}
                        className="w-20"
                      />
                      <span className="text-xs text-blue-700">weeks (pattern repeats after this)</span>
                    </div>

                    <div className="bg-white rounded border border-blue-300 p-3">
                      <Label className="text-sm font-medium mb-2 block">Week-by-Week Assignment:</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                        {(rotationForm.week_pattern || []).map((employeeId, weekIndex) => {
                          const employee = users.find(u => u.id === employeeId);
                          return (
                            <div key={weekIndex} className="space-y-1">
                              <Label className="text-xs text-gray-600">Week {weekIndex + 1}</Label>
                              <Select
                                value={employeeId}
                                onValueChange={(value) => updatePatternWeek(weekIndex, value)}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue>
                                    {employee ? employee.username : 'Select...'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {rotationForm.employee_shift_assignments.map((assignment) => {
                                    const user = users.find(u => u.id === assignment.employee_id);
                                    return (
                                      <SelectItem key={assignment.employee_id} value={assignment.employee_id}>
                                        {user ? user.username : assignment.employee_id}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        💡 Tip: Assign which employee gets the rotation shift each week. Others get their default shifts.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Days of Week */}
            <div className="space-y-2">
              <Label>Days of Week *</Label>
              <div className="flex items-center space-x-2">
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <Button
                    key={day}
                    type="button"
                    size="sm"
                    variant={rotationForm.days_of_week.includes(day) ? 'default' : 'outline'}
                    onClick={() => toggleDayOfWeek(day)}
                  >
                    {getDayName(day)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Day-Specific Templates */}
            {rotationForm.days_of_week.length > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3" data-testid="day-template-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-indigo-900 font-semibold">Per-Day Shift Templates</Label>
                    <p className="text-xs text-indigo-700 mt-1">
                      Assign different shift templates for different days (e.g., weekdays vs weekends)
                    </p>
                  </div>
                  {!useDayTemplates ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                      onClick={() => setUseDayTemplates(true)}
                      data-testid="enable-day-templates-btn"
                    >
                      Configure
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-gray-300 text-gray-600"
                      onClick={() => {
                        setUseDayTemplates(false);
                        setRotationForm(f => ({ ...f, day_template_overrides: {} }));
                      }}
                      data-testid="disable-day-templates-btn"
                    >
                      Use Single Template
                    </Button>
                  )}
                </div>
                {useDayTemplates && (
                  <div className="space-y-2 mt-2">
                    <p className="text-xs text-indigo-600">
                      Default: <span className="font-semibold">{shiftTemplates.find(t => t.id === rotationForm.rotation_shift_template_id)?.name || 'Not selected'}</span>. Override specific days below:
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {rotationForm.days_of_week.sort((a, b) => a - b).map((day) => {
                        const overrideId = rotationForm.day_template_overrides?.[String(day)] || '';
                        const isOverridden = !!overrideId;
                        return (
                          <div key={day} className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${isOverridden ? 'bg-white border border-indigo-200' : 'bg-indigo-50/50'}`} data-testid={`day-template-row-${day}`}>
                            <span className="text-sm font-medium w-12 text-indigo-900">{getDayName(day)}</span>
                            <select
                              value={overrideId}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRotationForm(f => {
                                  const overrides = { ...(f.day_template_overrides || {}) };
                                  if (val) {
                                    overrides[String(day)] = val;
                                  } else {
                                    delete overrides[String(day)];
                                  }
                                  return { ...f, day_template_overrides: overrides };
                                });
                              }}
                              className="flex-1 text-sm border rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
                              data-testid={`day-template-select-${day}`}
                            >
                              <option value="">Default ({shiftTemplates.find(t => t.id === rotationForm.rotation_shift_template_id)?.name || '—'})</option>
                              {shiftTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name} ({t.start_time} - {t.end_time}) [{t.shift_type === 'on_call' ? 'OC' : 'REG'}]
                                </option>
                              ))}
                            </select>
                            {isOverridden && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">Override</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Employee Selection */}
            <div className="space-y-2">
              <Label>Add Employees to Rotation *</Label>
              <Select onValueChange={addEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee to add" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => !rotationForm.employee_shift_assignments.find(a => a.employee_id === u.id))
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.username} ({user.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Order with Default Shifts */}
            {rotationForm.employee_shift_assignments.length > 0 && (
              <div className="space-y-2">
                <Label>Rotation Order & Default Shifts (Drag to reorder)</Label>
                <p className="text-xs text-gray-500">Assign each employee their default shift. During rotation weeks, they'll work the rotation shift instead.</p>
                <div className="space-y-3">
                  {rotationForm.employee_shift_assignments.map((assignment, index) => {
                    const template = shiftTemplates.find(t => t.id === assignment.default_shift_template_id);
                    return (
                      <div
                        key={assignment.employee_id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`border rounded-lg p-3 ${
                          draggedIndex === index ? 'bg-blue-50 border-blue-300' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <GripVertical className="h-5 w-5 text-gray-400 mt-2 cursor-move" />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-700">{index + 1}. </span>
                                <span className="text-gray-900">{getUserName(assignment.employee_id)}</span>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeEmployee(assignment.employee_id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">Default Shift for this employee *</Label>
                              <Select
                                value={assignment.default_shift_template_id}
                                onValueChange={(value) => updateEmployeeTemplate(assignment.employee_id, value)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select their default shift" />
                                </SelectTrigger>
                                <SelectContent>
                                  {shiftTemplates.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                      {t.name} ({t.start_time} - {t.end_time})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {template && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Works {template.start_time} - {template.end_time} normally
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setEditingRotation(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateRotation} disabled={loading}>
              {editingRotation ? 'Update Rotation' : 'Create Rotation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Rotation Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Rotation to Schedule</DialogTitle>
            <DialogDescription>
              Generate shifts for: {selectedRotation?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Duration Mode Selection */}
            <div className="space-y-2">
              <Label>Schedule Duration</Label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="apply-duration-mode"
                    value="weeks"
                    checked={applyForm.duration_mode === 'weeks'}
                    onChange={(e) => setApplyForm({...applyForm, duration_mode: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Number of Weeks</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="apply-duration-mode"
                    value="endDate"
                    checked={applyForm.duration_mode === 'endDate'}
                    onChange={(e) => setApplyForm({...applyForm, duration_mode: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">End Date</span>
                </label>
              </div>
            </div>

            {/* Number of Weeks (conditional) */}
            {applyForm.duration_mode === 'weeks' && (
              <div className="space-y-2">
                <Label htmlFor="weeks-to-generate">Number of Weeks to Generate</Label>
                <Input
                  id="weeks-to-generate"
                  type="number"
                  min="1"
                  max="52"
                  value={applyForm.weeks_to_generate}
                  onChange={(e) => setApplyForm({...applyForm, weeks_to_generate: parseInt(e.target.value) || 1})}
                  className="w-full"
                />
                {selectedRotation?.week_pattern && selectedRotation.week_pattern.length > 0 && (
                  <div className="bg-blue-50 p-2 rounded text-xs text-blue-700">
                    <p className="font-medium">💡 Pattern Tip:</p>
                    <p className="mt-1">
                      This rotation has a {selectedRotation.week_pattern.length}-week pattern. 
                      Consider using multiples: {[1, 2, 3, 4].map(mult => selectedRotation.week_pattern.length * mult).filter(w => w <= 52).join(', ')} weeks
                    </p>
                  </div>
                )}
                {!selectedRotation?.week_pattern && (
                  <p className="text-xs text-gray-500">
                    Typical options: 4, 8, 12, 16, 24, or 52 weeks
                  </p>
                )}
              </div>
            )}

            {/* End Date (conditional) */}
            {applyForm.duration_mode === 'endDate' && (
              <div>
                <Label htmlFor="rotation-end-date">End Date</Label>
                <Input
                  id="rotation-end-date"
                  type="date"
                  value={applyForm.end_date}
                  onChange={(e) => setApplyForm({...applyForm, end_date: e.target.value})}
                  min={selectedRotation?.start_date || new Date().toISOString().split('T')[0]}
                />
              </div>
            )}
            
            {/* Rotation Configuration Summary */}
            {selectedRotation && (
              <div className="bg-gray-50 border rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium text-gray-600">Rotation Configuration (saved):</p>
                <div className="flex flex-wrap gap-1">
                  {[0, 1, 2, 3, 4, 5, 6].map(d => {
                    const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d];
                    const isActive = selectedRotation.days_of_week?.includes(d);
                    return (
                      <span key={d} className={`px-2 py-0.5 rounded text-xs font-medium ${
                        isActive ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-400 line-through'
                      }`}>
                        {dayName}
                      </span>
                    );
                  })}
                </div>
                {selectedRotation.days_of_week && !selectedRotation.days_of_week.includes(5) && !selectedRotation.days_of_week.includes(6) && (
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    Weekends (Sat/Sun) are not included. Edit the rotation to add them.
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Start: {selectedRotation.start_date} | Employees: {selectedRotation.employee_shift_assignments?.length || 0}
                </p>
              </div>
            )}
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Note:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Existing shifts and approved PTO will be skipped</li>
                    <li>You'll see a conflict report after applying</li>
                    <li>Generated shifts can be edited or deleted individually</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Preview Table */}
            {preview && (
              <div className="border border-indigo-200 rounded-lg overflow-hidden" data-testid="rotation-preview">
                <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-indigo-900">
                    Preview: {preview.rotation_name} ({preview.total_weeks} weeks)
                  </span>
                  <span className="text-xs text-indigo-600">{preview.employee_count} employees</span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {preview.weeks.map((week, idx) => (
                    <div key={week.week_num ?? idx}>
                      <button
                        onClick={() => setPreviewExpanded(p => ({ ...p, [idx]: !p[idx] }))}
                        className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 text-left"
                        data-testid={`preview-week-${idx}`}
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-xs font-semibold text-gray-500 w-14">Wk {week.week_num}</span>
                          <span className="text-sm font-medium text-gray-900">{week.rotating_employee.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">{week.start_date} - {week.end_date}</span>
                          {previewExpanded[idx] ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                        </div>
                      </button>
                      {previewExpanded[idx] && (
                        <div className="px-4 pb-2 grid grid-cols-2 gap-1">
                          {week.days.map((day, di) => (
                            <div key={day.day_name || di} className="flex items-center space-x-2 text-xs py-1 px-2 rounded bg-gray-50">
                              <span className="font-medium w-10 text-gray-700">{day.day_name.slice(0, 3)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                day.shift_type === 'on_call' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {day.shift_type === 'on_call' ? 'OC' : 'REG'}
                              </span>
                              <span className="text-gray-600">{day.start_time}-{day.end_time}</span>
                              <span className="text-gray-400 truncate">{day.template_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApplyModal(false); setPreview(null); setPreviewExpanded({}); }}>
              Cancel
            </Button>
            <Button 
              variant="outline" 
              onClick={handlePreviewRotation} 
              disabled={previewLoading}
              data-testid="preview-rotation-btn"
            >
              {previewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
              Preview
            </Button>
            <Button onClick={handleApplyRotation} disabled={loading} data-testid="confirm-apply-rotation-btn">
              Apply Rotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RotationManagement;
